import {
  AfterViewInit,
  Component,
  Inject,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { tap } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import { MessageEditComponent } from 'src/app/messages/message-edit/message-edit.component';
import { Message } from 'src/app/models/message.model';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { ApiDataSource } from 'src/app/utils/api-data-source';
import { UserMessagesService } from '../user-messages.service';

@Component({
  selector: 'app-user-messages-view',
  templateUrl: './user-messages-view.component.html',
  styleUrls: ['./user-messages-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class UserMessagesViewComponent implements OnInit, AfterViewInit {
  message: Message = new Message();

  messageRecipientListHeaders: string[] = ['contact_person', 'employer'];
  messageRecipientListDataSource = new UserMessageRecipientsDataSource(
    this.app,
    this.api
  );

  // the paginator and sorter
  @ViewChild('paginator1') paginator1: MatPaginator;
  @ViewChild('paginator2') paginator2: MatPaginator;

  constructor(
    public app: AppService,
    public api: ApiRequestService,
    public utils: UtilsService,
    private dialogRef: MatDialogRef<MessageEditComponent>,
    @Inject(MAT_DIALOG_DATA) private dialogData: any,
    private userMessagesService: UserMessagesService
  ) {}

  ngOnInit(): void {
    // Get the message id
    this.message.id = Number(this.dialogData['messageId']);
    // Validate and make a request to get the message data
    if (!this.message.id) {
      this.utils.showToast('The message could not be opened.');
      this.dialogRef.close();
      return;
    }
  }

  ngAfterViewInit(): void {
    // Stop if the message id is not set.
    if (!this.message.id) {
      return;
    }

    // Make the API request to get the message.
    this.api
      .makeRequest('get', `v2/user/messages/${this.message.id}`)
      .then(
        (message: Message): void => {
          this.message.apply(message);
          // Get the messages meta
          this.userMessagesService.getMessagesMeta();

          this.messageRecipientListDataSource.messageId = this.message.id;
          this.messageRecipientListDataSource.getData(true);
        }
      )
      .catch(() => {
        this.utils.showToast('The message could not be opened.');
        this.dialogRef.close();
      });

    // Define the paginator tap events.
    const paginatorTap = tap((paginator) => {
      this.paginator1.pageIndex = paginator['pageIndex'];
      this.paginator1.pageSize = paginator['pageSize'];
      this.paginator2.pageIndex = paginator['pageIndex'];
      this.paginator2.pageSize = paginator['pageSize'];

      this.messageRecipientListDataSource.limit = paginator['pageSize'];
      this.messageRecipientListDataSource.offset = paginator['pageIndex'];
      this.messageRecipientListDataSource.getData();
    });

    // Subscribe to the paginator tap events.
    this.paginator1.page.pipe(paginatorTap).subscribe();
    this.paginator2.page.pipe(paginatorTap).subscribe();
  }

  /**
   * Opens the specified URL using the utility function.
   *
   * @param {string} url - The URL to be opened.
   * @return {void} This method does not return a value.
   */
  onOpenUrl(url: string): void {
    this.utils.openUrl(url);
  }

  /**
   * Notifies the user that the URL has been copied to their clipboard via a toast message.
   *
   * @return {void} Does not return any value.
   */
  onCopiedURL(): void {
    this.utils.showToast('The URL is copied to your clipboard.');
  }
}

export class UserMessageRecipientsDataSource extends ApiDataSource {
  messageId: number = 0;

  getData(resetOffset: boolean = false) {
    this.makeRequest(
      `v2/user/messages/${this.messageId}/recipients`,
      resetOffset
    );
  }
}
